import { useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useFetch } from "../../lib/useData";
import { PageTitle, Badge, TableWrap, Btn, Modal, Input, Textarea, Select } from "./ui";
const PAGE_SIZES = [10, 50, 100, 500, 1000];
// DB mein sirf path (/api/uploads/xyz.jpg) hota hai; yahan full URL banta hai
export const imgSrc = (p) => {
  if (!p) return "";
  if (/^(https?:)?\/\//.test(p) || p.startsWith("data:") || p.startsWith("blob:")) return p; // purane full URLs bhi chalenge
  return `${process.env.REACT_APP_BACKEND_URL || ""}${p}`;
};

// Resize big camera photos before upload (max 1920px, JPEG)
// Resize big photos before upload (GIF/SVG/AVIF/BMP ko as-is bhejta hai)
const compressImage = (file, maxSize = 1920, quality = 0.85) =>
  new Promise((resolve) => {
    const skip = ["image/gif", "image/svg+xml", "image/avif", "image/bmp"];
    if (skip.includes(file.type) || file.size < 1024 * 1024) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      const type = file.type === "image/png" ? "image/png" : "image/jpeg";
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          if (!blob || blob.size >= file.size) return resolve(file);
          const ext = type === "image/png" ? ".png" : ".jpg";
          resolve(new File([blob], file.name.replace(/\.\w+$/, ext), { type }));
        },
        type,
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

export default function CrudManager({ title, desc, collection, fields, columns, defaults = {}, module, uniqueFields = [] }) {
  const { data, loading, reload } = useFetch(`/admin/${collection}`);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  

  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const allRows = data || [];
  const total = allRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pagedRows = allRows.slice(start, start + pageSize);

  const openNew = () => { setUploading(false); setForm({ status: "draft", ...defaults }); setEditing("new"); };
  const openEdit = (row) => { setUploading(false); setForm({ ...row }); setEditing(row.id); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (name, original, inputEl) => {
    if (!original) return;
    setUploading(true);
    try {
      const file = await compressImage(original);
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { timeout: 60000 });
set(name, res.data.path);
      toast.success("Image uploaded");
    } catch (e) {
      console.error("UPLOAD ERROR:", e);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Upload failed");
    } finally {
      setUploading(false);
      if (inputEl) inputEl.value = "";
    }
  };

  const save = async () => {
    // ===== duplicate check START =====
    for (const f of uniqueFields) {
      const val = form[f.name];
      if (val === "" || val === undefined || val === null) continue;
      const clash = (data || []).find(
        (it) => String(it[f.name]) === String(val) && it.id !== editing
      );
      if (clash) {
        toast.error(
          `${f.label} ${val} already exists (${clash.name || clash.title || "another item"}). Please choose a different ${f.label}.`
        );
        return;
      }
    }
    // ===== duplicate check END =====

    setSaving(true);
    try {
      if (editing === "new") await api.post(`/admin/${collection}`, form);
      else await api.put(`/admin/${collection}/${editing}`, form);
      toast.success("Saved");
      setEditing(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.name || row.title || row.question || "item"}"?`)) return;
    try { await api.delete(`/admin/${collection}/${row.id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const togglePublish = async (row) => {
    try {
      await api.put(`/admin/${collection}/${row.id}`, { ...row, status: row.status === "published" ? "draft" : "published" });
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title={title} desc={desc}>
        <Btn onClick={openNew} testid={`${collection}-add`}><Plus className="w-4 h-4" /> Add New</Btn>
      </PageTitle>

      <TableWrap>
        <thead className="bg-slate-800 text-slate-400">
          <tr>
            {columns.map((c) => <th key={c.key} className="text-left px-4 py-3">{c.label}</th>)}
            <th className="text-left px-4 py-3">Status</th>
            <th className="text-right px-4 py-3">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-700" data-testid={`${collection}-table`}>
          {loading ? <tr><td colSpan={columns.length + 2} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr> :
           pagedRows.map((row) => (
              <tr key={row.id} className="hover:bg-slate-800/50">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3">
                    {c.image ? <img src={imgSrc(row[c.key])} alt="" className="w-12 h-12 rounded-lg object-cover" /> :
                      <span className="text-slate-200">{c.render ? c.render(row[c.key], row) : String(row[c.key] ?? "—").slice(0, 60)}</span>}
                  </td>
                ))}
                <td className="px-4 py-3"><Badge status={row.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => togglePublish(row)} className="p-2 rounded-lg hover:bg-slate-700 text-slate-300" title="Publish/Unpublish" data-testid={`${collection}-toggle-${row.id}`}>{row.status === "published" ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                    <button onClick={() => openEdit(row)} className="p-2 rounded-lg hover:bg-slate-700 text-blue-400" data-testid={`${collection}-edit-${row.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(row)} className="p-2 rounded-lg hover:bg-slate-700 text-red-400" data-testid={`${collection}-delete-${row.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          {!loading && (data || []).length === 0 && <tr><td colSpan={columns.length + 2} className="px-4 py-8 text-center text-slate-500">No items yet. Click “Add New”.</td></tr>}
        </tbody>
      </TableWrap>

      {!loading && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-slate-300">
          <div className="flex items-center gap-2">
            <span>Show</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-200"
              data-testid={`${collection}-page-size`}
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>per page</span>
          </div>
          <span className="text-slate-400">
            {start + 1}–{Math.min(start + pageSize, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >Prev</button>
            <span>{currentPage} / {totalPages}</span>
            <button
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >Next</button>
          </div>
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? `Add ${title}` : `Edit ${title}`} wide>
        <div className="grid sm:grid-cols-2 gap-4">
          {fields.map((f) => {
            const common = { label: f.label, value: form[f.name], onChange: (v) => set(f.name, v), testid: `field-${f.name}` };
            const span = f.span === 2 ? "sm:col-span-2" : "";
            return (
              <div key={f.name} className={span}>
                {f.type === "textarea" ? <Textarea {...common} rows={f.rows || 4} /> :
                f.type === "select" ? (
                  <Select
                    {...common}
                    value={form[f.name] === undefined ? undefined : String(form[f.name])}
                    onChange={(v) => {
                      const match = f.options.find((o) => String(typeof o === "object" ? o.value : o) === v);
                      set(f.name, match === undefined ? v : (typeof match === "object" ? match.value : match));
                    }}
                    options={f.options.map((o) =>
                      typeof o === "object"
                        ? { ...o, value: String(o.value), label: o.label ?? String(o.value) }
                        : { value: String(o), label: String(o) }
                    )}
                  />
                ) :
                f.type === "status" ? <Select {...common} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} /> :
               f.type === "image" ? (
  <div>
    <label className="block text-sm text-slate-300 mb-1.5">{f.label}</label>
    <input
      type="file"
      accept="image/*"
      disabled={uploading}
      onChange={(e) => uploadImage(f.name, e.target.files?.[0], e.target)}
      className="block w-full text-sm text-slate-300 file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-orange-600 file:text-white hover:file:bg-orange-500 bg-slate-900 border border-slate-700 rounded-lg"
      data-testid={`upload-${f.name}`}
    />
    {uploading && <p className="text-xs text-slate-400 mt-1">Uploading…</p>}
    {!uploading && form[f.name] && (
      <div className="mt-2 flex items-center gap-3">
       <img src={imgSrc(form[f.name])} alt="" className="w-20 h-20 rounded-lg object-cover" />
        <button
          type="button"
          onClick={() => set(f.name, "")}
          className="text-xs text-red-400 hover:underline"
        >
          Remove image
        </button>
      </div>
    )}
  </div>
) :
                f.type === "number" ? <Input {...common} type="number" onChange={(v) => set(f.name, Number(v))} /> :
                <Input {...common} />}
               
                {f.hint && <p className="text-xs text-slate-500 mt-1">{f.hint}</p>}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} testid={`${collection}-save`}>{saving ? "Saving…" : "Save"}</Btn>
        </div>
      </Modal>
    </div>
  );
}